import { jwtDecode } from "jwt-decode";
import useUserTokenStore from "@/stores/user-token.store";
import {
  isRecoveringAuth,
  recoverFromInteractiveAuthError,
  recoverFromMissingOrganization,
  type LoginWithRedirect,
} from "@/lib/auth/authRecovery";
import { parseTenantIdFromAccessToken } from "@/utils/auth";
import {
  requireAccessToken,
  type GetAccessTokenSilently,
} from "@/lib/auth/accessToken";
import { hasAuthRedirectSearchParams } from "@/lib/oauth/isAuthCallbackSearch";

/**
 * Margin before a token's `exp` at which we consider it stale and refresh early.
 * Keeps in-flight requests from sending a token that expires mid-request.
 */
const EXPIRY_BUFFER_MS = 30_000;

type TokenWithExp = { exp?: number; permissions?: string[]; org_id?: string };

const syncAccessTokenToStore = (token: string): void => {
  useUserTokenStore.getState().setToken(token);
  try {
    const { permissions } = jwtDecode<TokenWithExp>(token);
    useUserTokenStore.getState().setPermissions(permissions ?? []);
  } catch {
    useUserTokenStore.getState().setPermissions([]);
  }
};

/**
 * The Auth0 organization id (`org_...`) from the standard `org_id` claim of the
 * persisted access token. Passed to silent auth so the SDK's `prompt=none`
 * iframe fallback can resolve the organization without the `organization_hint`
 * cookie, which expires 24h after login and otherwise dead-ends in
 * `interaction_required` (LAR-310). NOTE: this is the Auth0 org id, distinct
 * from the Lara/tenant org id in the `https://markup.legal/organization_id`
 * claim — see CONTEXT.md.
 */
export const getPersistedOrganizationId = (): string | undefined => {
  const token = useUserTokenStore.getState().token;
  if (!token) return undefined;
  try {
    return jwtDecode<TokenWithExp>(token).org_id;
  } catch {
    return undefined;
  }
};

/**
 * The Auth0 organization id from the SDK user object (`useAuth0().user`), whose
 * `org_id` claim isn't in the SDK's `User` type. Used as a fallback when the
 * persisted access token isn't in the store yet (LAR-310).
 */
export const getSdkOrganizationId = (user: unknown): string | undefined => {
  const orgId = (user as { org_id?: unknown } | null | undefined)?.org_id;
  return typeof orgId === "string" ? orgId : undefined;
};

/**
 * Auth0 options that carry the organization into a *silent* token call so the
 * `prompt=none` iframe fallback resolves without the 24h `organization_hint`
 * cookie (LAR-310). Prefers the persisted store token; falls back to the SDK's
 * ID-token claim so org survives when our Zustand copy is empty. Returns
 * `undefined` (no org) when neither source has one.
 */
export const buildSilentAuthOptions = (
  sdkOrganizationId?: string
): { authorizationParams: { organization: string } } | undefined => {
  const organization = getPersistedOrganizationId() ?? sdkOrganizationId;
  return organization ? { authorizationParams: { organization } } : undefined;
};

/** Fast rejection used once the interactive-login circuit-breaker has tripped. */
class AuthRecoveryInProgressError extends Error {
  readonly error = "auth_recovery_in_progress";
  constructor() {
    super("Interactive login in progress; skipping silent token refresh");
    this.name = "AuthRecoveryInProgressError";
  }
}

/** Rejection used while Auth0 has not finished resolving the session. */
class AuthNotReadyError extends Error {
  readonly error = "auth_not_ready";
  constructor() {
    super("Auth0 is still resolving the session; no token to serve yet");
    this.name = "AuthNotReadyError";
  }
}

/**
 * True while Auth0 is mid-login: the URL still carries the `?code=&state=` the provider is about to
 * exchange. The hook adds the provider's own `isLoading` to this.
 */
const isHandlingAuthRedirect = (): boolean => hasAuthRedirectSearchParams();

/** Rejection used when the token we hold carries no organization and cannot be sent. */
class OrganizationRequiredError extends Error {
  readonly error = "organization_required";
  constructor() {
    super(
      "Access token carries no organization; tenant endpoints would reject it"
    );
    this.name = "OrganizationRequiredError";
  }
}

/**
 * Gate every token the getter hands out on the Lara organization claim.
 *
 * A token without it is not usable: the API rejects it with 403 on every tenant-scoped endpoint
 * (LAR-437), so passing it on turns one recoverable problem into a page full of failed queries with
 * nothing driving recovery. Trigger the interactive login once instead — it either comes back
 * org-scoped, or the Auth0 action denies it with a reason the sign-in screen shows — and reject the
 * caller meanwhile, which `restClient` reports as a synthetic 401 that `useQueryMe` leaves alone
 * (LAR-361).
 */
const requireOrganizationScope = (
  token: string,
  loginWithRedirect: LoginWithRedirect
): string => {
  if (parseTenantIdFromAccessToken(token) !== null) return token;

  recoverFromMissingOrganization(loginWithRedirect);
  throw new OrganizationRequiredError();
};

/**
 * The Auth0 SDK's own cached token, or null when it has none. Kept separate from the freshness
 * checks below so a rejection from {@link requireOrganizationScope} is not swallowed by the
 * cache-miss catch.
 *
 * A miss resolves `undefined` — it does not throw — so it must be normalised to null here. Handing
 * `undefined` on as a token wiped the stored one and then read as "no organization", which sent
 * the user back to an interactive login on every page load that raced the SDK's cache: the cache is
 * empty until the `?code=` exchange completes, so a login loop landed the user back on the sign-in
 * screen (LAR-437).
 */
const readSdkCachedToken = async (
  getAccessTokenSilently: GetAccessTokenSilently
): Promise<string | null> => {
  try {
    return (await getAccessTokenSilently({ cacheMode: "cache-only" })) || null;
  } catch {
    return null;
  }
};

/**
 * The silent refresh currently in flight, shared by every caller.
 *
 * Module-level for the same reason as the recovery breaker: one tab has one Auth0
 * session and one refresh-token family, so two concurrent refreshes can only race
 * each other into a rotation-reuse breach. Without this, a tab regaining focus
 * fired one refresh per waking query — measured at up to 30 in a 10-second window
 * — and Auth0 revoked the family (LAR-361; the open Layer 4 of LAR-290).
 */
let inFlightRefresh: Promise<string> | null = null;

/**
 * Run a silent refresh, or join the one already running. The shared promise is
 * cleared as soon as it settles, so the next stale-token read starts a new one.
 */
export const refreshAccessTokenOnce = (
  getAccessTokenSilently: GetAccessTokenSilently,
  sdkOrganizationId?: string
): Promise<string> => {
  if (inFlightRefresh) return inFlightRefresh;

  const refresh = getAccessTokenSilently(
    buildSilentAuthOptions(sdkOrganizationId)
  )
    .then((maybeToken) => {
      // Same contract gap as the cache-only read above: nothing downstream may receive a
      // non-token, so fail the refresh instead of caching and returning one.
      const token = requireAccessToken(maybeToken, "refreshAccessTokenOnce");
      syncAccessTokenToStore(token);
      return token;
    })
    .finally(() => {
      inFlightRefresh = null;
    });

  inFlightRefresh = refresh;
  return refresh;
};

/** Test seam: drop any shared in-flight refresh. */
export const resetInFlightRefresh = (): void => {
  inFlightRefresh = null;
};

const isTokenFresh = (token: string): boolean => {
  if (!token) return false;
  try {
    const { exp } = jwtDecode<TokenWithExp>(token);
    if (!exp) return false;
    return exp * 1000 - Date.now() > EXPIRY_BUFFER_MS;
  } catch {
    return false;
  }
};

/**
 * Wraps the Auth0 SDK's `getAccessTokenSilently` so callers serve the cached
 * access token from the user-token store (kept fresh centrally by
 * `TokenRefresher`) and only fall back to the SDK when the cached token is
 * missing or within {@link EXPIRY_BUFFER_MS} of expiry.
 *
 * This collapses the refresh drivers to a single source in the common case:
 * without it, every API request independently called `getAccessTokenSilently`,
 * and concurrent refresh-token use (e.g. several Edge tabs waking at once)
 * triggered Auth0 rotation breach detection and forced logouts (LAR-290).
 */
export const createCachedTokenGetter = (
  getAccessTokenSilently: GetAccessTokenSilently,
  loginWithRedirect: LoginWithRedirect,
  sdkOrganizationId?: string,
  isAuthNotReady: () => boolean = isHandlingAuthRedirect
): (() => Promise<string>) => {
  return async () => {
    // Nothing can be served while Auth0 is still resolving the session, and recovery must not fire
    // there: the SDK holds no cache entry yet, and a silent call made after a logout has no
    // organization to resolve, so Auth0 answers `interaction_required`. Recovering from that starts
    // a *second* interactive login on top of the one the user just finished — they type their email,
    // land back mid-exchange, and get sent to Auth0 again (LAR-437). Reject retryably instead; the
    // caller's query retries once the provider has settled.
    if (isAuthNotReady()) {
      throw new AuthNotReadyError();
    }

    // Once an interactive re-login is under way, don't re-hit the failing
    // silent-auth path on every request — that is the "External interaction
    // required" flood (LAR-290). Reject fast until the redirect navigates away.
    if (isRecoveringAuth()) {
      throw new AuthRecoveryInProgressError();
    }

    const cached = useUserTokenStore.getState().token;

    // Auth0's localStorage cache can update in another tab (refresh) or after
    // org switch while the Zustand store still holds the previous JWT.
    const sdkToken = await readSdkCachedToken(getAccessTokenSilently);
    if (sdkToken !== null && sdkToken !== cached) {
      syncAccessTokenToStore(sdkToken);
      return requireOrganizationScope(sdkToken, loginWithRedirect);
    }

    if (isTokenFresh(cached))
      return requireOrganizationScope(cached, loginWithRedirect);

    let refreshed: string;
    try {
      refreshed = await refreshAccessTokenOnce(
        getAccessTokenSilently,
        sdkOrganizationId
      );
    } catch (error) {
      // Interactive-required (rotation breach / dead silent-auth): recover to an
      // interactive login exactly once instead of looping. Transient errors
      // fall through so callers can apply their own bounded retry.
      recoverFromInteractiveAuthError(error, loginWithRedirect);
      throw error;
    }

    return requireOrganizationScope(refreshed, loginWithRedirect);
  };
};
